import { useMutation, useQuery } from '@apollo/client'
import KeyboardDoubleArrowRightTwoToneIcon from '@mui/icons-material/KeyboardDoubleArrowRightTwoTone'
import { Box, Button, Stack } from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Trans } from 'react-i18next'
import { gql } from '~/__generated__'
import { RecommendationRow as RecommendationRowType } from '~/__generated__/graphql'
import { SectionLabel } from '~/components/SectionLabel'
import { DefaultCardGroup, RankingCarGroup } from '~/features/video/components/CardGroup'
import { useMediaQuery } from '~/hooks/useMediaQuery'

const GET_RECOMMENDATION_ROW_QUERY = gql(`
  query getRecommendationRow($id: ID!) {
    recommendationRow(id: $id) {
      id
      videos {
        id
        status
        satisfiedNum
        duration {
          minutes
          seconds
        }
        viewingTime {
          id
          viewingMin
          viewingSecond
        }
        landscapeThumbnail
        portraitThumbnail
        willBeUnpublishedAt
        description
      }
    }
  }
`)

const UPDATE_RECOMMENDATION_ROW_PARAM = gql(`
  mutation updateRecommendationRowParam($input: UpdateRecommendationRowParamMutationInput!) {
    updateRecommendationRowParam(input:  $input) {
      success
    }
  }
`)

type Props = {
  data: RecommendationRowType
  recommendationSessionKey: string
  deferUntilVisible: boolean
  /** ビデオカードをクリック */
  onClickVideoItem: (videoId: number, isKeyVisual: boolean) => void
  /** カテゴリー横の『全て表示』をクリック */
  onClickDisplayAll?: () => void
}

export const RecommendationRow = ({
  data,
  recommendationSessionKey,
  onClickDisplayAll,
  onClickVideoItem,
  deferUntilVisible,
}: Props) => {
  const [hasEnteredFetchRange, setHasEnteredFetchRange] = useState(!deferUntilVisible)
  const { data: recommendationRowData } = useQuery(GET_RECOMMENDATION_ROW_QUERY, {
    variables: { id: data.id },
    skip: !hasEnteredFetchRange,
  })
  const [updateRecommendationRowParam] = useMutation(UPDATE_RECOMMENDATION_ROW_PARAM)
  const { isSm } = useMediaQuery()

  const videos = useMemo(
    () => recommendationRowData?.recommendationRow.videos ?? undefined,
    [recommendationRowData],
  )

  const handleConversion = () => {
    updateRecommendationRowParam({
      variables: {
        input: {
          id: data.id,
          recommendationSessionKey,
          eventType: 'CONVERSION',
        },
      },
    })
  }

  const handleClickDisplayAll = onClickDisplayAll
    ? () => {
        handleConversion()
        onClickDisplayAll()
      }
    : undefined

  const handleClickVideoItem = (videoId: number, isKeyVisual: boolean) => {
    handleConversion()
    onClickVideoItem(videoId, isKeyVisual)
  }

  const rowRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (videos === undefined || videos.length === 0) return

    const el = rowRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (!entry?.isIntersecting) return

        updateRecommendationRowParam({
          variables: {
            input: {
              id: data.id,
              recommendationSessionKey,
              eventType: 'IMPRESSION',
            },
          },
        })
      },
      {
        threshold: 0.1,
      },
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [data.id, recommendationSessionKey, updateRecommendationRowParam, videos])

  useEffect(() => {
    if (hasEnteredFetchRange) return
    const el = rowRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return
        setHasEnteredFetchRange(true)
        observer.disconnect()
      },
      {
        rootMargin: '400px 0px',
      },
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [hasEnteredFetchRange])

  if (videos && videos.length === 0) {
    return null
  }

  return (
    <Stack spacing={1} ref={rowRef}>
      <Box
        display='flex'
        justifyContent={{ sm: 'flex-start' }}
        gap={{ xs: 0, sm: 1 }}
        onClick={isSm ? undefined : handleClickDisplayAll}
      >
        <SectionLabel>{data.name}</SectionLabel>
        {handleClickDisplayAll && (
          <Button onClick={isSm ? handleClickDisplayAll : undefined} sx={{ minHeight: 0 }}>
            {isSm ? (
              <Trans i18nKey='show_all' />
            ) : (
              <KeyboardDoubleArrowRightTwoToneIcon fontSize='large' />
            )}
          </Button>
        )}
      </Box>
      {data.rankable ? (
        <RankingCarGroup
          videos={videos}
          onClickVideoItem={handleClickVideoItem}
          onGetConversion={handleConversion}
        />
      ) : (
        <DefaultCardGroup
          videos={videos}
          onClickVideoItem={handleClickVideoItem}
          onGetConversion={handleConversion}
        />
      )}
    </Stack>
  )
}
